import { css } from '@emotion/react';
import {
  Alpha,
  Hue, Saturation,
} from '@uiw/react-color';
import {
  type FC, useCallback, useEffect, useMemo, useRef,
} from 'react';
import { inputCommonStyle } from '~/_shared/baseComponents/inputs/textInput/textInput.styles';
import { MaptiveColorPickerBarPointerComponent } from '~/_shared/components/colorPicker/maptiveColorPicker/maptiveColorPickerBarPointer.component';
import { MaptiveColorPickerCirclePointerComponent } from '~/_shared/components/colorPicker/maptiveColorPicker/maptiveColorPickerCirclePointer.component';
import { useThrottle } from '~/_shared/utils/hooks/useThrottle';
import { FPS30 } from '~/_shared/utils/throttle/throttle';
import { type Theme } from '../../../themes/theme.model';
import { useClickOutsideListenerRef } from '../../../utils/hooks/useClickOutsideListenerRef';
import { ColorCodeHexFieldComponent } from '../colorCodeFields/colorCodeHexField.component';
import {
  createColor, getMaxAmountOfColors, rgbColorToString,
} from '../colorPicker.helpers';
import {
  type ColorResult, type HsvaColor, type RgbaColor,
} from '../colorPicker.types';

const COLOR_PICKER_WIDTH = 200;
const COLOR_PICKER_PADDING = 10;
const COLOR_TILES_WRAPPER_PADDING = 10;
const SATURATION_PANEL_HEIGHT = 150;
const PRESET_COLOR_TILE_DIMENSIONS = 16;
const SELECTED_COLOR_TILE_DIMENSION = 24;
const SELECTED_COLOR_TILE_MARGIN = 4;
const COLOR_TILES_AND_INPUT_HEIGHT = 2 * PRESET_COLOR_TILE_DIMENSIONS + COLOR_TILES_WRAPPER_PADDING;
const PRESET_COLORS_CONTAINER_HEIGHT = COLOR_TILES_AND_INPUT_HEIGHT + 2 * COLOR_TILES_WRAPPER_PADDING;

export const MAPTIVE_COLOR_PICKER_WIDTH = COLOR_PICKER_WIDTH + COLOR_PICKER_PADDING;
export const MAPTIVE_COLOR_PICKER_HEIGHT = SELECTED_COLOR_TILE_DIMENSION + SELECTED_COLOR_TILE_MARGIN
  + SATURATION_PANEL_HEIGHT + COLOR_TILES_WRAPPER_PADDING + PRESET_COLORS_CONTAINER_HEIGHT;

const coloPickerStyle = (theme: Theme) => css({
  position: 'relative',
  backgroundColor: theme.colorPickerColors.backgroundColor,
  borderRadius: '4px',
  width: `${COLOR_PICKER_WIDTH}px`,
  padding: `${COLOR_PICKER_PADDING}px ${COLOR_PICKER_PADDING}px 0px`,
  boxShadow: `${theme.colorPickerColors.shadowColorPrimary} 0px 0px 0px 1px,
              ${theme.colorPickerColors.shadowColorPrimary} 0px 8px 16px`,
  zIndex: 1,
});

const saturationStyle = css({
  position: 'relative',
  width: '200px',
  height: `${SATURATION_PANEL_HEIGHT}px`,
});

const alphaStyle = css({
  marginTop: 4,
});

const hueAlphaTileWrapperStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
});

const slidersWrapperStyle = css({ padding: '4px 0px' });

const selectedColorTileStyle = ({ color }: { color: RgbaColor}) => (theme: Theme) => css({
  backgroundColor: rgbColorToString(color),
  borderRadius: '3px',
  boxShadow: `${theme.colorPickerColors.shadowColorPrimary} 0px 0px 0px 1px inset,
              ${theme.colorPickerColors.shadowColorSecondary} 0px 0px 4px inset`,
  width: `${SELECTED_COLOR_TILE_DIMENSION}px`,
  height: `${SELECTED_COLOR_TILE_DIMENSION}px`,
  margin: `${SELECTED_COLOR_TILE_MARGIN}px 0 0 ${SELECTED_COLOR_TILE_MARGIN}px`,
});

const colorsAndInputStyle = css({
  display: 'flex',
  position: 'relative',
  boxSizing: 'border-box',
  height: PRESET_COLORS_CONTAINER_HEIGHT,
});

const colorTilesWrapperStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  position: 'relative',
  margin: `0px -${COLOR_TILES_WRAPPER_PADDING}px`,
  padding: `${COLOR_TILES_WRAPPER_PADDING}px 0px 0px ${COLOR_TILES_WRAPPER_PADDING}px`,
  width: '60%',
});

const hexInputWrapperStyle = css({
  display: 'flex',
  position: 'absolute',
  bottom: COLOR_TILES_WRAPPER_PADDING,
  marginLeft: -COLOR_TILES_WRAPPER_PADDING,
  right: 0,

  '&:focus-visible': {
    outline: 'none',
  },
});

const hexInputStyle = (theme: Theme) => css(inputCommonStyle(theme, true), {
  backgroundColor: theme.colorPickerColors.backgroundColor,
  border: `1px solid ${theme.colorPickerColors.shadowColorInput}`,
  boxSizing: 'border-box',
  color: theme.colorPickerColors.textColor,
  fontSize: 16,
  fontWeight: 500,
  height: COLOR_TILES_AND_INPUT_HEIGHT,
  outline: 'none',
  padding: '4px 10% 3px',
  width: PRESET_COLOR_TILE_DIMENSIONS * 4 + COLOR_TILES_WRAPPER_PADDING * 3,
});

const presetColorTileStyle = ({ color }: { color: RgbaColor }) => (theme: Theme) => css({
  backgroundColor: rgbColorToString(color),
  borderRadius: '3px',
  height: PRESET_COLOR_TILE_DIMENSIONS,
  margin: '0px 10px 10px 0px',
  width: PRESET_COLOR_TILE_DIMENSIONS,
  boxShadow: `${theme.colorPickerColors.shadowColorPrimary} 0px 0px 0px 1px inset`,
  '&:focus': {
    boxShadow: `${theme.colorPickerColors.shadowColorPrimary} 0px 0px 0px 1px inset,
                ${color} 0px 0px 4px`,
  },
});

type CustomColorPickerProps = Readonly<{
  className?: string;
  displayAlpha?: boolean;
  displayNumericalColorsRepresentation: boolean;
  presetColors: readonly string[];
  selectedColor: ColorResult;

  onChange: (color: ColorResult) => void;
  onClose: () => void;
  setRef?: (ref?: HTMLDivElement | null) => void;
}>;

const CustomColorPickerComponent: FC<CustomColorPickerProps> = ({
  selectedColor,
  onChange,
  ...props
}) => {
  const colorPickerRef = useClickOutsideListenerRef(props.onClose, true);

  const presetColors = useMemo(() =>
    getMaxAmountOfColors(props.presetColors),
  [props.presetColors]);

  useEffect(() => {
    const setRef = props.setRef;
    setRef?.(colorPickerRef?.current);
  }, [colorPickerRef, props.setRef]);

  const handleOnChange = useCallback((newColor: HsvaColor) => {
    onChange(createColor(newColor));
  }, [onChange]);

  return (
    <div
      className={props.className}
      css={coloPickerStyle}
      ref={colorPickerRef}
    >
      <div css={saturationStyle} >
        <Saturation
          hsva={selectedColor.hsva}
          onChange={handleOnChange}
          style={{
            width: COLOR_PICKER_WIDTH,
            height: SATURATION_PANEL_HEIGHT,
          }}
          pointer={MaptiveColorPickerCirclePointerComponent}
          prefixCls="color-picker-saturation"
        />
      </div>
      <span css={hueAlphaTileWrapperStyle}>
        <span css={slidersWrapperStyle}>
          <div>
            <Hue
              hue={selectedColor.hsva.h}
              onChange={({ h }) => {
                handleOnChange({
                  ...selectedColor.hsva,
                  h,
                });
              }}
              pointer={MaptiveColorPickerBarPointerComponent}
              height={10}
              width={170}
              innerProps={{
                style: { marginLeft: 1, marginRight: 5 },
              }}
            />
          </div>
          {props.displayAlpha && (
            <div css={alphaStyle}>
              <Alpha
                hsva={selectedColor.hsva}
                onChange={({ a }) => {
                  handleOnChange({
                    ...selectedColor.hsva,
                    a,
                  });
                }}
                width={170}
                pointer={MaptiveColorPickerBarPointerComponent}
                style={{
                  height: 10,
                  backgroundColor: 'transparent',
                }}
                height={10}
                innerProps={{
                  style: { marginLeft: 1, marginRight: 5 },
                }}
              />
            </div>
          )}
        </span>
        <div css={selectedColorTileStyle({ color: selectedColor.rgba })} />
      </span>
      <div css={colorsAndInputStyle}>
        <div css={colorTilesWrapperStyle} >
          {presetColors.map((hex: string, index: number) => {
            const color = createColor(hex);

            return (
              <div
                key={hex}
                tabIndex={index}
                css={presetColorTileStyle({ color: color.rgba })}
                onClick={() => onChange(color)}
              />
            );
          })}
        </div>
        <div css={hexInputWrapperStyle}>
          <ColorCodeHexFieldComponent
            css={hexInputStyle}
            value={selectedColor}
            onChange={onChange}
          />
        </div>
      </div>
    </div>
  );
};

export type MaptiveColorPickerProps = Readonly<{
  selectedColor: ColorResult;
  isFixed?: boolean;
  className?: string;
  displayAlpha?: boolean;
  presetColors?: readonly string[];
  displayNumericalColorsRepresentation?: boolean;

  onChange?: (color: ColorResult) => void;
  onClose?: () => void;
  setRef?: (ref?: HTMLDivElement) => void;
}>;

export const MaptiveColorPickerComponent: FC<MaptiveColorPickerProps> = props => {
  const presetColors = useMemo(() => props.presetColors ?? [], [props.presetColors]);

  const onClose = useCallback((): void => {
    const onClose = props.onClose;
    onClose?.();
  }, [props.onClose]);

  const onChangeRef = useRef(props.onChange);
  onChangeRef.current = props.onChange;

  const onColorChange = useThrottle((color: ColorResult): void => {
    onChangeRef.current?.(color);
  }, [], FPS30);

  return (
    <CustomColorPickerComponent
      {...props}
      onClose={onClose}
      presetColors={presetColors}
      onChange={onColorChange}
      displayNumericalColorsRepresentation={!!props.displayNumericalColorsRepresentation}
    />
  );
};
